/**
 * Pure builder for `POST /api/shipping/rates`.
 *
 * Translates a buyer-side intent (single seller + long-form ship-to address +
 * combined parcel) into the wire shape `@kraahl/contract` defines. Framework-
 * free — importable from any test runner with no React/Next mocks (hex §1).
 *
 * Why a builder instead of inline assembly in the hook:
 *   - HARD GATE / Silent NACK: components never assemble wire bodies inline.
 *     The hook calls this; UI calls the hook (never this directly).
 *   - Sibling-E: positive tests of the hook must call the REAL builder, not a
 *     hand-written literal that pins the bug they're trying to catch.
 *   - Adding a second consumer (server-side resolver pre-flight, mobile, etc.)
 *     gets the same long-form→ISO normalisation for free.
 *
 * The single business decision here is country-name → ISO alpha-2 normalisation.
 * `CheckoutIntentAddress.country` is long-form (e.g. `"United States"`) per the
 * checkout-intent contract; `ShippingRatesAddress.country` is ISO alpha-2.
 *
 * **Pure normaliser — never throws.** The builder no longer carries
 * destination-gate responsibility: that lives on the server (`rates.ts`
 * → `NO_RATES_TO_DESTINATION` per [ORIGIN_COUNTRY_SUPPORT §5.3](../../.tondocs/shippo/ORIGIN_COUNTRY_SUPPORT.md)
 * + [PLAN §A](../../.tondocs/shippo/PLAN.md) "Geography" + "Checkout fallback"
 * rows). Under Option D the gate is the SELLER's origin tier, not the buyer's
 * destination — US-origin sellers ship worldwide. An older NA-only whitelist
 * here threw `OutOfRegionError` for non-US/CA destinations, which made the
 * Phase 2a `NO_RATES_TO_DESTINATION` UI copy unreachable (left-fix gap from
 * Phase 1a `4642bafb`; closed by this commit).
 *
 * Normalisation strategy:
 *   1. Long-form name in PayPal country table → ISO alpha-2 (most common path
 *      — the buyer-side address form populates from this table)
 *   2. 2-char string → uppercased and passed through (ISO-shaped already)
 *   3. Anything else → passed through unchanged; server Zod schema
 *      (`country: z.string().min(2)`) rejects pathological inputs, and Shippo
 *      returns 0 rates for unknown ISO codes → `NO_RATES_TO_DESTINATION`.
 *
 * @see .tondocs/shippo/PLAN.md §A — Geography (by origin tier, Option D)
 * @see .tondocs/shippo/PLAN.md §A — Checkout fallback (no silent legacy)
 * @see .tondocs/shippo/ORIGIN_COUNTRY_SUPPORT.md §5.3 — server is the gate
 * @see .tondocs/shippo/PLAN.md §D WS4 — workstream
 * @see .tondocs/shippo/PLAN.md §J.5 — single-seller invariant
 * @see .tondocs/shippo/PLAN.md §L — rate is ESTIMATE at this stage
 * @see .tondocs/skills/hex.md §1 — pure domain, no framework imports
 * @see .tondocs/skills/type-driven-design.md §5 — Parse Don't Validate
 * @see packages/contract/src/checkout-intent.ts — long-form country origin
 * @see packages/contract/src/shipping-rates.ts — ISO country destination
 * @see lib/shipping/rates.ts — server-side `RateFetchResult` ADT (authoritative gate)
 */

import type {
  ShippingRatesAddress,
  ShippingRatesParcel,
  ShippingRatesRequest,
} from "@kraahl/contract";
import { getCountryCodeByName } from "@/lib/paypal-countries";

/**
 * Intent shape the buyer-side hook consumes. Mirrors the in-app types the
 * cart / checkout already has (single seller, long-form country, combined
 * parcel dims) — translation to the contract shape happens here.
 */
export interface ShippingRateIntent {
  sellerId: string;
  address: {
    name: string;
    street1: string;
    street2?: string;
    city: string;
    state: string;
    zip: string;
    /** Long-form (e.g. `"United States"`) OR ISO alpha-2 (`"US"`). */
    country: string;
    /**
     * Consignee phone (E.164 preferred, e.g. `"+33612345678"`). Optional at
     * the intent layer — buyer-side wiring composes it from
     * `phoneCountryCode + phoneNumber` when both are present. Threaded
     * through to Shippo so DHL Express + USPS Priority Mail Intl don't drop
     * the shipment for missing consignee phone (2026-07-03 US→FR probe).
     */
    phone?: string;
    /** Consignee email. Optional. Threaded through to Shippo when present. */
    email?: string;
  };
  parcel: ShippingRatesParcel;
}

/**
 * Normalise a country value (long-form OR ISO alpha-2) to ISO alpha-2.
 *
 * Total function — never throws. Unknown inputs pass through verbatim; the
 * server boundary (Zod `country: z.string().min(2)`) catches structural junk,
 * and Shippo's rate response is the authoritative destination gate
 * (0 rates → `NO_RATES_TO_DESTINATION`).
 *
 * Exported for the rare consumer that wants to reuse the same normalisation
 * outside the builder (e.g. preview UI). Pure; safe to call anywhere.
 */
export function normaliseCountryToIso(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return trimmed;
  // Long-form name → ISO via the PayPal country table (~220 entries).
  // `getCountryCodeByName` returns the input unchanged on miss, so we detect
  // a hit by string inequality.
  const fromName = getCountryCodeByName(trimmed);
  if (fromName !== trimmed) return fromName;
  // Already 2-char → assume ISO alpha-2, uppercase to canonicalise.
  if (trimmed.length === 2) return trimmed.toUpperCase();
  // Unknown shape — pass through. Server is the gate.
  return trimmed;
}

/**
 * Assemble the wire body for `POST /api/shipping/rates`.
 *
 * Total function — never throws. Single-seller invariant lives in the input
 * shape (one `sellerId` field, not an array). Destination eligibility is
 * the server's responsibility ([ORIGIN_COUNTRY_SUPPORT §5.3](../../.tondocs/shippo/ORIGIN_COUNTRY_SUPPORT.md)).
 */
export function buildShippingRatesRequest(
  intent: ShippingRateIntent,
): ShippingRatesRequest {
  const iso = normaliseCountryToIso(intent.address.country);

  const address: ShippingRatesAddress = {
    name: intent.address.name,
    street1: intent.address.street1,
    ...(intent.address.street2 ? { street2: intent.address.street2 } : {}),
    city: intent.address.city,
    state: intent.address.state,
    zip: intent.address.zip,
    country: iso,
    // Only forward phone/email when non-empty — Zod `.optional()` on the
    // schema accepts absent; sending `""` would be surface-equivalent to
    // "buyer has no phone" and defeats the DHL-drop fix.
    ...(intent.address.phone && intent.address.phone.trim().length > 0
      ? { phone: intent.address.phone.trim() }
      : {}),
    ...(intent.address.email && intent.address.email.trim().length > 0
      ? { email: intent.address.email.trim() }
      : {}),
  };

  return {
    sellerId: intent.sellerId,
    address,
    parcel: intent.parcel,
  };
}
